import { motion } from "motion/react";
import { type ReactNode } from "react";
import innerText from "react-innertext";
import { readingTime } from "reading-time-estimator";

export interface PatternsLinePlayerProps extends BoxProps {
  lines: ReactNode[];
  followOn: ReactNode;
  delay?: number;
}

const MotionLi = motion.create(List.Item);

const PatternsLinePlayer: FC<PatternsLinePlayerProps> = ({
  lines,
  followOn,
  className,
  delay = 1000,
  ...otherProps
}) => {
  const [onscreenLines, setOnscreenLines] = useState<ReactNode[]>([]);
  const [finished, setFinished] = useState(false);

  // == Play
  const nextPlayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const play = useCallback((lines: ReactNode[]) => {
    const [line, ...remainingLines] = lines;
    if (!line) {
      setFinished(true);
      return;
    }
    setOnscreenLines(prevLines => [...prevLines, line]);
    const lineText = innerText(line);
    const { words } = readingTime(lineText);
    const timeout = Math.max((words / 250) * 60 * 1000, 2000);
    nextPlayTimeoutRef.current = setTimeout(() => {
      nextPlayTimeoutRef.current = null;
      play(remainingLines);
    }, timeout);
  }, []);
  useEffect(() => {
    let playTimeout: NodeJS.Timeout | undefined;
    if (delay) {
      playTimeout = setTimeout(() => {
        play(lines);
      }, delay);
    } else {
      play(lines);
    }
    return () => {
      if (playTimeout) {
        clearTimeout(playTimeout);
      }
      const nextPlayTimeout = nextPlayTimeoutRef.current;
      if (nextPlayTimeout) {
        clearTimeout(nextPlayTimeout);
      }
      setOnscreenLines([]);
      setFinished(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // == Scroll to bottom
  useEffect(scrollToBottom, [onscreenLines.length]);

  return (
    <Stack className={cn("PatternsLinePlayer", className)} {...otherProps}>
      <List
        listStyleType="none"
        style={{ display: "flex", flexDirection: "column", gap: 6 }}
      >
        {onscreenLines.map((line, i) => (
          <MotionLi
            key={i}
            variants={{
              hidden: {
                opacity: 0,
              },
              visible: {
                opacity: 1,
              },
            }}
            initial="hidden"
            animate="visible"
          >
            {line}
          </MotionLi>
        ))}
      </List>
      {finished && <>{followOn}</>}
    </Stack>
  );
};

export default PatternsLinePlayer;
